/**
 * Fotos de evidência (0.1.88) — usadas nas Reclamações (chamados) e nas Notificações.
 *
 * Mostra as miniaturas (toque abre a foto grande numa aba nova), e — para quem pode —
 * "Adicionar foto" (no celular abre a câmera ou a galeria) e apagar. O servidor reduz a
 * foto e tira a localização GPS e os dados do aparelho antes de guardar.
 */
import { useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { api, ApiError, fotoUrl, type FotoAnexo } from "../lib/api";

export const MAX_FOTOS = 4;

/** Envia as fotos escolhidas para um registro já criado. */
export async function enviarFotos(caminho: "chamados" | "notificacoes", id: number, arquivos: File[]) {
  if (!arquivos.length) return;
  const fd = new FormData();
  arquivos.forEach((f) => fd.append("arquivos", f));
  await api.postForm(`/${caminho}/${id}/fotos`, fd);
}

/** Botão "Foto" para formulários de criação (guarda os arquivos até salvar o registro). */
export function EscolherFotos({ arquivos, onChange }: { arquivos: File[]; onChange: (f: File[]) => void }) {
  const ref = useRef<HTMLInputElement | null>(null);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button type="button" onClick={() => ref.current?.click()} disabled={arquivos.length >= MAX_FOTOS}
        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 disabled:opacity-40">
        <Camera size={15} /> Foto
      </button>
      <input ref={ref} type="file" accept="image/*" multiple hidden
        onChange={(e) => {
          const novos = Array.from(e.target.files ?? []);
          onChange([...arquivos, ...novos].slice(0, MAX_FOTOS));
          e.target.value = "";
        }} />
      {arquivos.map((f, i) => (
        <span key={i} className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded-md px-2 py-1">
          {f.name.length > 18 ? f.name.slice(0, 16) + "…" : f.name}
          <button type="button" onClick={() => onChange(arquivos.filter((_, k) => k !== i))}
            className="text-slate-400 hover:text-rose-600">×</button>
        </span>
      ))}
      {arquivos.length > 0 && <span className="text-[11px] text-slate-400">até {MAX_FOTOS} fotos</span>}
    </div>
  );
}

/** Galeria de um registro existente, com adicionar/apagar conforme a permissão. */
export function FotosEvidencia({ caminho, registroId, fotos, podeEnviar, podeApagar, onMudou }: {
  caminho: "chamados" | "notificacoes";
  registroId: number;
  fotos: FotoAnexo[];
  podeEnviar: boolean;
  podeApagar: boolean;
  onMudou: () => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  if (!fotos.length && !podeEnviar) return null;

  const falha = (e: unknown) => setErro(e instanceof ApiError ? e.message : "Não foi possível concluir.");
  async function adicionar(arquivos: File[]) {
    setOcupado(true); setErro(null);
    try { await enviarFotos(caminho, registroId, arquivos.slice(0, MAX_FOTOS - fotos.length)); onMudou(); }
    catch (e) { falha(e); }
    finally { setOcupado(false); }
  }
  async function apagar(id: number) {
    if (!confirm("Apagar esta foto?")) return;
    setOcupado(true); setErro(null);
    try { await api.delete(`/fotos/${id}`); onMudou(); }
    catch (e) { falha(e); }
    finally { setOcupado(false); }
  }

  return (
    <div className="mt-2">
      <div className="flex flex-wrap gap-2">
        {fotos.map((f) => (
          <div key={f.id} className="relative">
            <a href={fotoUrl(f.id)} target="_blank" rel="noreferrer" title="Abrir a foto">
              <img src={fotoUrl(f.id)} alt="Foto anexada" loading="lazy"
                className="w-20 h-20 object-cover rounded-lg border border-slate-200 bg-slate-100" />
            </a>
            {podeApagar && (
              <button onClick={() => apagar(f.id)} disabled={ocupado} title="Apagar foto"
                className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 rounded-full p-1 text-rose-500 hover:bg-rose-50">
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
        {podeEnviar && fotos.length < MAX_FOTOS && (
          <button onClick={() => ref.current?.click()} disabled={ocupado}
            className="w-20 h-20 rounded-lg border border-dashed border-slate-300 text-slate-500 text-xs flex flex-col items-center justify-center gap-1 hover:bg-slate-50 disabled:opacity-50">
            {ocupado ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
            Foto
          </button>
        )}
        <input ref={ref} type="file" accept="image/*" multiple hidden
          onChange={(e) => { const a = Array.from(e.target.files ?? []); e.target.value = ""; if (a.length) adicionar(a); }} />
      </div>
      {erro && <p className="text-xs text-rose-600 mt-1">{erro}</p>}
    </div>
  );
}
